/**
 * TemplateMenuBuilderPage — samodzielna strona projektowania domyslnego menu modulu.
 *
 * Cienki wrapper: Layout + naglowek (wstecz/tytul) + <MenuBuilderEmbedded>.
 * Logika i stan przeniesione do MenuBuilderEmbedded (reuzywalne inline w edytorze).
 *
 * Routing: `/module-templates/:slug/menu-builder`.
 */

import { useTranslation } from 'react-i18next';
import { useNavigate, useParams } from 'react-router-dom';
import { ArrowLeft } from 'lucide-react';

import { Layout } from '@/components/Layout';
import { MenuBuilderEmbedded } from '@/components/menu/MenuBuilderEmbedded';
import { useModuleTemplateDetail } from '@/lib/module-templates/template-layouts';

export function TemplateMenuBuilderPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { slug = '' } = useParams<{ slug: string }>();

  const detailQuery = useModuleTemplateDetail(slug);

  const entities = detailQuery.data?.entities ?? [];
  const templateName = detailQuery.data?.name;
  const defaultMenu = detailQuery.data?.defaultMenu ?? [];

  return (
    <Layout>
      <section
        className="flex h-[calc(100vh-6.5rem)] flex-col px-6"
        data-testid="template-menu-builder-page"
      >
        <header className="mb-3 flex shrink-0 items-center gap-3">
          <button
            type="button"
            onClick={() => navigate(`/module-templates/${slug}`)}
            className="rounded p-1.5 text-gray-500 hover:bg-gray-100 dark:text-gray-400 dark:hover:bg-gray-800"
            aria-label={t('admin.templateMenuBuilder.back')}
            data-testid="template-menu-builder-back"
          >
            <ArrowLeft className="h-5 w-5" />
          </button>
          <div className="flex-1">
            <h1 className="text-xl font-semibold">{t('admin.templateMenuBuilder.title')}</h1>
            <p className="mt-0.5 text-sm text-gray-500 dark:text-gray-400">
              {templateName ?? slug}
            </p>
          </div>
        </header>

        {detailQuery.isError ? (
          <div className="flex flex-1 items-center justify-center text-sm text-red-600">
            {t('admin.templateMenuBuilder.loadError')}
          </div>
        ) : detailQuery.isLoading ? null : (
          <div className="flex min-h-0 flex-1 flex-col overflow-hidden">
            <MenuBuilderEmbedded
              templateSlug={slug}
              entities={entities}
              initialMenu={defaultMenu}
            />
          </div>
        )}
      </section>
    </Layout>
  );
}
